// ============================================================================
// PSEUDOCODE LIBRARY
// Production-grade pseudocode for algorithm categories that are not covered by
// dedicated pseudocode files. This is referenced by codeGenerator.ts.
// ============================================================================

export const PSEUDOCODE_LIBRARY: Record<string, string> = {
  shell: `procedure shellSort(A)
    n := length(A)
    gap := floor(n / 2)
    while gap > 0 do
        for i := gap to n - 1 do
            temp := A[i]
            j := i
            while j >= gap and A[j - gap] > temp do
                A[j] := A[j - gap]
                j := j - gap
            A[j] := temp
        gap := floor(gap / 2)`,

  counting: `procedure countingSort(A)
    max := maximum value in A
    count := array of size max + 1 initialized to 0
    for each x in A do
        count[x] := count[x] + 1
    index := 0
    for i := 0 to max do
        while count[i] > 0 do
            A[index] := i
            index := index + 1
            count[i] := count[i] - 1`,

  radix: `procedure radixSort(A)
    max := maximum value in A
    exp := 1
    while max / exp > 0 do
        countingSortByDigit(A, exp)
        exp := exp * 10

procedure countingSortByDigit(A, exp)
    output := array of size length(A)
    count := array of size 10 initialized to 0
    for i := 0 to length(A) - 1 do
        digit := floor(A[i] / exp) mod 10
        count[digit] := count[digit] + 1
    for i := 1 to 9 do
        count[i] := count[i] + count[i - 1]
    for i := length(A) - 1 downto 0 do
        digit := floor(A[i] / exp) mod 10
        output[count[digit] - 1] := A[i]
        count[digit] := count[digit] - 1
    copy output into A`,

  bucket: `procedure bucketSort(A)
    n := length(A)
    buckets := array of n empty lists
    for i := 0 to n - 1 do
        bucketIndex := floor(n * A[i] / (max(A) + 1))
        append A[i] to buckets[bucketIndex]
    for each bucket in buckets do
        sort bucket using insertion sort
    concatenate all buckets into A`,

  cocktail: `procedure cocktailSort(A)
    swapped := true
    start := 0
    end := length(A) - 1
    while swapped do
        swapped := false
        for i := start to end - 1 do
            if A[i] > A[i + 1] then
                swap A[i] and A[i + 1]
                swapped := true
        if not swapped then break
        end := end - 1
        swapped := false
        for i := end - 1 downto start do
            if A[i] > A[i + 1] then
                swap A[i] and A[i + 1]
                swapped := true
        start := start + 1`,

  gnome: `procedure gnomeSort(A)
    index := 0
    while index < length(A) do
        if index == 0 or A[index] >= A[index - 1] then
            index := index + 1
        else
            swap A[index] and A[index - 1]
            index := index - 1`,

  comb: `procedure combSort(A)
    gap := length(A)
    shrink := 1.3
    swapped := false
    while gap > 1 or swapped do
        gap := max(1, floor(gap / shrink))
        swapped := false
        for i := 0 to length(A) - gap - 1 do
            if A[i] > A[i + gap] then
                swap A[i] and A[i + gap]
                swapped := true`,

  bellmanford: `procedure BellmanFord(Graph, source)
    distance[source] := 0
    for each vertex v in Graph do
        distance[v] := INFINITY
    for i := 1 to |V| - 1 do
        for each edge (u, v) with weight w in Graph do
            if distance[u] + w < distance[v] then
                distance[v] := distance[u] + w
    for each edge (u, v) with weight w in Graph do
        if distance[u] + w < distance[v] then
            error "Graph contains negative-weight cycle"
    return distance`,

  prim: `procedure Prim(Graph, source)
    for each vertex v in Graph do
        key[v] := INFINITY
        parent[v] := NULL
        inMST[v] := false
    key[source] := 0
    priorityQueue := all vertices keyed by key
    while priorityQueue is not empty do
        u := vertex in priorityQueue with minimum key
        remove u from priorityQueue
        inMST[u] := true
        for each neighbor v of u with weight w do
            if not inMST[v] and w < key[v] then
                key[v] := w
                parent[v] := u
                update priorityQueue
    return parent`,

  kruskal: `procedure Kruskal(Graph)
    result := empty set
    for each vertex v in Graph do
        makeSet(v)
    sort all edges by weight in non-decreasing order
    for each edge (u, v) with weight w in sorted order do
        if find(u) != find(v) then
            add edge (u, v) to result
            union(u, v)
    return result`,

  astar: `procedure AStar(Graph, start, goal)
    openSet := {start}
    gScore[start] := 0
    fScore[start] := heuristic(start, goal)
    while openSet is not empty do
        current := node in openSet with lowest fScore
        if current == goal then
            return reconstructPath(current)
        remove current from openSet
        for each neighbor of current do
            tentativeG := gScore[current] + cost(current, neighbor)
            if tentativeG < gScore[neighbor] then
                cameFrom[neighbor] := current
                gScore[neighbor] := tentativeG
                fScore[neighbor] := tentativeG + heuristic(neighbor, goal)
                if neighbor not in openSet then
                    add neighbor to openSet
    return failure`,

  topological: `procedure topologicalSort(Graph)
    inDegree := compute in-degree for every vertex
    queue := all vertices with inDegree 0
    result := empty list
    while queue is not empty do
        u := queue.dequeue()
        append u to result
        for each neighbor v of u do
            inDegree[v] := inDegree[v] - 1
            if inDegree[v] == 0 then
                queue.enqueue(v)
    if length(result) < |V| then
        error "Graph contains a cycle"
    return result`,

  floydwarshall: `procedure FloydWarshall(Graph)
    n := number of vertices
    dist := n x n matrix initialized with edge weights
    for i := 0 to n - 1 do
        dist[i][i] := 0
    for k := 0 to n - 1 do
        for i := 0 to n - 1 do
            for j := 0 to n - 1 do
                if dist[i][k] + dist[k][j] < dist[i][j] then
                    dist[i][j] := dist[i][k] + dist[k][j]
    return dist`,

  scc: `procedure Kosaraju(Graph)
    visited := empty set
    order := empty stack
    for each vertex v in Graph do
        if v not in visited then
            DFS1(Graph, v, visited, order)
    transposeGraph := reverse all edges of Graph
    visited := empty set
    stronglyConnectedComponents := empty list
    while order is not empty do
        v := order.pop()
        if v not in visited then
            component := empty set
            DFS2(transposeGraph, v, visited, component)
            add component to stronglyConnectedComponents
    return stronglyConnectedComponents

procedure DFS1(Graph, v, visited, order)
    visited.add(v)
    for each neighbor u of v do
        if u not in visited then
            DFS1(Graph, u, visited, order)
    order.push(v)

procedure DFS2(Graph, v, visited, component)
    visited.add(v)
    component.add(v)
    for each neighbor u of v do
        if u not in visited then
            DFS2(Graph, u, visited, component)`,

  dsu: `procedure makeSet(x)
    parent[x] := x
    rank[x] := 0

procedure find(x)
    if parent[x] != x then
        parent[x] := find(parent[x])
    return parent[x]

procedure union(x, y)
    rootX := find(x)
    rootY := find(y)
    if rootX == rootY then return
    if rank[rootX] < rank[rootY] then
        parent[rootX] := rootY
    else if rank[rootX] > rank[rootY] then
        parent[rootY] := rootX
    else
        parent[rootY] := rootX
        rank[rootX] := rank[rootX] + 1`,

  lis: `procedure LIS(A)
    n := length(A)
    dp := array of size n initialized to 1
    for i := 1 to n - 1 do
        for j := 0 to i - 1 do
            if A[j] < A[i] and dp[j] + 1 > dp[i] then
                dp[i] := dp[j] + 1
    return max value in dp`,

  coinchange: `procedure coinChange(coins, amount)
    dp := array of size amount + 1 initialized to INFINITY
    dp[0] := 0
    for i := 1 to amount do
        for each coin in coins do
            if coin <= i and dp[i - coin] != INFINITY then
                dp[i] := min(dp[i], dp[i - coin] + 1)
    return dp[amount] if not INFINITY else -1`,

  matrixchain: `procedure matrixChainMultiplication(dimensions)
    n := length(dimensions) - 1
    dp := n x n matrix initialized to 0
    for length := 2 to n do
        for i := 0 to n - length do
            j := i + length - 1
            dp[i][j] := INFINITY
            for k := i to j - 1 do
                cost := dp[i][k] + dp[k + 1][j] + dimensions[i] * dimensions[k + 1] * dimensions[j + 1]
                dp[i][j] := min(dp[i][j], cost)
    return dp[0][n - 1]`,

  partition: `procedure partitionEqualSubsetSum(nums)
    total := sum(nums)
    if total is odd then return false
    target := total / 2
    dp := array of size target + 1 initialized to false
    dp[0] := true
    for each num in nums do
        for j := target downto num do
            dp[j] := dp[j] or dp[j - num]
    return dp[target]`,

  wordbreak: `procedure wordBreak(s, wordDict)
    n := length(s)
    dp := array of size n + 1 initialized to false
    dp[0] := true
    for i := 1 to n do
        for each word in wordDict do
            if i >= length(word) and dp[i - length(word)] and substring(s, i - length(word), i) == word then
                dp[i] := true
    return dp[n]`,

  lps: `procedure longestPalindromicSubsequence(s)
    n := length(s)
    dp := n x n matrix initialized to 0
    for i := 0 to n - 1 do
        dp[i][i] := 1
    for length := 2 to n do
        for i := 0 to n - length do
            j := i + length - 1
            if s[i] == s[j] then
                dp[i][j] := dp[i + 1][j - 1] + 2
            else
                dp[i][j] := max(dp[i + 1][j], dp[i][j - 1])
    return dp[0][n - 1]`,

  kmp: `procedure KMP(text, pattern)
    lps := computeLPS(pattern)
    i := 0
    j := 0
    while i < length(text) do
        if pattern[j] == text[i] then
            i := i + 1
            j := j + 1
        if j == length(pattern) then
            print "Pattern found at index " + (i - j)
            j := lps[j - 1]
        else if i < length(text) and pattern[j] != text[i] then
            if j != 0 then
                j := lps[j - 1]
            else
                i := i + 1

procedure computeLPS(pattern)
    lps := array of size length(pattern) initialized to 0
    length := 0
    i := 1
    while i < length(pattern) do
        if pattern[i] == pattern[length] then
            length := length + 1
            lps[i] := length
            i := i + 1
        else if length != 0 then
            length := lps[length - 1]
        else
            lps[i] := 0
            i := i + 1
    return lps`,

  rabinkarp: `procedure RabinKarp(text, pattern, prime)
    n := length(text)
    m := length(pattern)
    h := pow(d, m - 1) mod prime
    p := hash(pattern) mod prime
    t := hash(text[0..m-1]) mod prime
    for i := 0 to n - m do
        if p == t then
            if text[i..i+m-1] == pattern then
                print "Pattern found at index " + i
        if i < n - m then
            t := (d * (t - text[i] * h) + text[i + m]) mod prime
            if t < 0 then t := t + prime`,

  boyermoore: `procedure BoyerMoore(text, pattern)
    lastOccurrence := buildLastOccurrenceTable(pattern)
    i := length(pattern) - 1
    while i < length(text) do
        j := length(pattern) - 1
        while j >= 0 and text[i] == pattern[j] do
            i := i - 1
            j := j - 1
        if j < 0 then
            print "Pattern found at index " + (i + 1)
            i := i + length(pattern) + 1
        else
            shift := lastOccurrence[text[i]] or length(pattern)
            i := i + max(1, shift - j)`,

  naivestring: `procedure naiveStringSearch(text, pattern)
    n := length(text)
    m := length(pattern)
    for i := 0 to n - m do
        j := 0
        while j < m and text[i + j] == pattern[j] do
            j := j + 1
        if j == m then
            print "Pattern found at index " + i`,

  sieve: `procedure sieveOfEratosthenes(n)
    isPrime := array of size n + 1 initialized to true
    isPrime[0] := false
    isPrime[1] := false
    for p := 2 to sqrt(n) do
        if isPrime[p] then
            for i := p * p to n step p do
                isPrime[i] := false
    return all i where isPrime[i] is true`,

  gcd: `procedure gcd(a, b)
    while b != 0 do
        temp := b
        b := a mod b
        a := temp
    return a`,

  fastexponent: `procedure fastExponentiation(base, exponent, mod)
    result := 1
    base := base mod mod
    while exponent > 0 do
        if exponent mod 2 == 1 then
            result := (result * base) mod mod
        base := (base * base) mod mod
        exponent := floor(exponent / 2)
    return result`,

  modular: `procedure modularArithmetic(a, b, mod, operation)
    if operation == "add" then
        return (a + b) mod mod
    if operation == "subtract" then
        return ((a - b) mod mod + mod) mod mod
    if operation == "multiply" then
        return (a * b) mod mod
    if operation == "divide" then
        return (a * modularInverse(b, mod)) mod mod`,

  activity: `procedure activitySelection(activities)
    sort activities by finish time
    selected := empty list
    lastFinishTime := 0
    for each activity in sorted activities do
        if activity.start >= lastFinishTime then
            add activity to selected
            lastFinishTime := activity.finish
    return selected`,

  fractionalknapsack: `procedure fractionalKnapsack(items, capacity)
    for each item in items do
        ratio := item.value / item.weight
    sort items by ratio descending
    totalValue := 0
    for each item in sorted items do
        if capacity >= item.weight then
            totalValue := totalValue + item.value
            capacity := capacity - item.weight
        else
            totalValue := totalValue + ratio * capacity
            break
    return totalValue`,

  huffman: `procedure HuffmanCoding(chars, freqs)
    priorityQueue := min-heap of nodes keyed by frequency
    for each char with frequency do
        insert leaf node into priorityQueue
    while priorityQueue size > 1 do
        left := extractMin(priorityQueue)
        right := extractMin(priorityQueue)
        merged := new internal node with frequency left.freq + right.freq
        merged.left := left
        merged.right := right
        insert merged into priorityQueue
    return extractMin(priorityQueue)`,

  linearsearch: `procedure linearSearch(A, target)
    for i := 0 to length(A) - 1 do
        if A[i] == target then
            return i
    return NOT_FOUND`,

  nqueens: `procedure solveNQueens(n)
    board := n x n matrix initialized to 0
    placeQueen(board, 0)

procedure placeQueen(board, row)
    if row == n then
        print board
        return
    for col := 0 to n - 1 do
        if isSafe(board, row, col) then
            board[row][col] := 1
            placeQueen(board, row + 1)
            board[row][col] := 0

procedure isSafe(board, row, col)
    for i := 0 to row - 1 do
        if board[i][col] == 1 then return false
        if col - (row - i) >= 0 and board[i][col - (row - i)] == 1 then return false
        if col + (row - i) < n and board[i][col + (row - i)] == 1 then return false
    return true`,

  sudoku: `procedure solveSudoku(board)
    for row := 0 to 8 do
        for col := 0 to 8 do
            if board[row][col] == 0 then
                for num := 1 to 9 do
                    if isValid(board, row, col, num) then
                        board[row][col] := num
                        if solveSudoku(board) then return true
                        board[row][col] := 0
                return false
    return true

procedure isValid(board, row, col, num)
    for i := 0 to 8 do
        if board[row][i] == num or board[i][col] == num then return false
    boxRow := 3 * floor(row / 3)
    boxCol := 3 * floor(col / 3)
    for i := 0 to 2 do
        for j := 0 to 2 do
            if board[boxRow + i][boxCol + j] == num then return false
    return true`,

  convexhull: `procedure convexHull(points)
    if length(points) < 3 then return points
    hull := empty stack
    sort points by x-coordinate (and by y-coordinate if ties)
    for each point in sorted order do
        while hull has at least 2 points and cross(hull[-2], hull[-1], point) <= 0 do
            hull.pop()
        hull.push(point)
    lowerSize := length(hull)
    for each point in reverse sorted order do
        while length(hull) > lowerSize and cross(hull[-2], hull[-1], point) <= 0 do
            hull.pop()
        hull.push(point)
    return hull`,

  segmenttree: `procedure buildSegmentTree(arr, node, start, end)
    if start == end then
        tree[node] := arr[start]
    else
        mid := (start + end) / 2
        buildSegmentTree(arr, 2 * node, start, mid)
        buildSegmentTree(arr, 2 * node + 1, mid + 1, end)
        tree[node] := tree[2 * node] + tree[2 * node + 1]

procedure querySegmentTree(node, start, end, left, right)
    if start > right or end < left then return 0
    if left <= start and end <= right then return tree[node]
    mid := (start + end) / 2
    return query(2 * node, start, mid, left, right) + query(2 * node + 1, mid + 1, end, left, right)`,

  fenwick: `procedure FenwickTree(n)
    tree := array of size n + 1 initialized to 0

procedure update(tree, index, delta)
    while index <= n do
        tree[index] := tree[index] + delta
        index := index + (index & -index)

procedure query(tree, index)
    sum := 0
    while index > 0 do
        sum := sum + tree[index]
        index := index - (index & -index)
    return sum`,

  trie: `procedure trieInsert(root, word)
    current := root
    for each char in word do
        if current.children[char] is null then
            current.children[char] := new TrieNode()
        current := current.children[char]
    current.isEnd := true

procedure trieSearch(root, word)
    current := root
    for each char in word do
        if current.children[char] is null then return false
        current := current.children[char]
    return current.isEnd`,

  bst: `procedure BSTInsert(root, key)
    if root is null then return newNode(key)
    if key < root.key then
        root.left := BSTInsert(root.left, key)
    else if key > root.key then
        root.right := BSTInsert(root.right, key)
    return root

procedure BSTSearch(root, key)
    if root is null or root.key == key then return root
    if key < root.key then return BSTSearch(root.left, key)
    return BSTSearch(root.right, key)`,

  avl: `procedure AVLInsert(root, key)
    if root is null then return newNode(key)
    if key < root.key then root.left := AVLInsert(root.left, key)
    else if key > root.key then root.right := AVLInsert(root.right, key)
    else return root
    root.height := 1 + max(height(root.left), height(root.right))
    balance := height(root.left) - height(root.right)
    if balance > 1 and key < root.left.key then return rightRotate(root)
    if balance < -1 and key > root.right.key then return leftRotate(root)
    if balance > 1 and key > root.left.key then
        root.left := leftRotate(root.left)
        return rightRotate(root)
    if balance < -1 and key < root.right.key then
        root.right := rightRotate(root.right)
        return leftRotate(root)
    return root`,

  bitonic: `procedure bitonicSort(A, low, count, direction)
    if count > 1 then
        k := count / 2
        bitonicSort(A, low, k, ASCENDING)
        bitonicSort(A, low + k, k, DESCENDING)
        bitonicMerge(A, low, count, direction)

procedure bitonicMerge(A, low, count, direction)
    if count > 1 then
        k := greatestPowerOfTwoLessThan(count)
        for i := low to low + count - k - 1 do
            if (A[i] > A[i + k]) == direction then
                swap A[i] and A[i + k]
        bitonicMerge(A, low, k, direction)
        bitonicMerge(A, low + k, count - k, direction)`,

  cycle: `procedure cycleSort(A)
    n := length(A)
    writes := 0
    for cycleStart := 0 to n - 2 do
        item := A[cycleStart]
        pos := cycleStart
        for i := cycleStart + 1 to n - 1 do
            if A[i] < item then pos := pos + 1
        if pos == cycleStart then continue
        while item == A[pos] do pos := pos + 1
        swap A[pos] and item; writes := writes + 1
        while pos != cycleStart do
            pos := cycleStart
            for i := cycleStart + 1 to n - 1 do
                if A[i] < item then pos := pos + 1
            while item == A[pos] do pos := pos + 1
            swap A[pos] and item; writes := writes + 1
    return writes`,

  'odd-even': `procedure oddEvenSort(A)
    n := length(A)
    sorted := false
    while not sorted do
        sorted := true
        for i := 1 to n - 2 step 2 do
            if A[i] > A[i + 1] then
                swap A[i] and A[i + 1]
                sorted := false
        for i := 0 to n - 2 step 2 do
            if A[i] > A[i + 1] then
                swap A[i] and A[i + 1]
                sorted := false`,

  pigeonhole: `procedure pigeonholeSort(A)
    min := minimum value in A
    max := maximum value in A
    range := max - min + 1
    holes := array of size range initialized to 0
    for each x in A do
        holes[x - min] := holes[x - min] + 1
    index := 0
    for i := 0 to range - 1 do
        while holes[i] > 0 do
            A[index] := i + min
            index := index + 1
            holes[i] := holes[i] - 1`,

  stooge: `procedure stoogeSort(A, l, h)
    if A[l] > A[h] then
        swap A[l] and A[h]
    if h - l + 1 > 2 then
        t := floor((h - l + 1) / 3)
        stoogeSort(A, l, h - t)
        stoogeSort(A, l + t, h)
        stoogeSort(A, l, h - t)`,

  tim: `procedure timSort(A)
    RUN := 32
    n := length(A)
    for i := 0 to n step RUN do
        insertionSort(A, i, min(i + RUN - 1, n - 1))
    size := RUN
    while size < n do
        for left := 0 to n step 2 * size do
            mid := min(left + size - 1, n - 1)
            right := min(left + 2 * size - 1, n - 1)
            if mid < right then
                merge(A, left, mid, right)
        size := size * 2`,

  library: `procedure librarySort(A)
    gaps := array with gap positions between elements
    for each element in A do
        pos := binarySearch(gaps, element)
        if gap available at pos then
            insert element at pos
        else
            rebalance(gaps)
            insert element at correct position
    return elements without gaps`,

  bubble: `procedure bubbleSort(A)
    n := length(A)
    repeat
        swapped := false
        for i := 1 to n - 1 do
            if A[i - 1] > A[i] then
                swap A[i - 1] and A[i]
                swapped := true
        n := n - 1
    until not swapped`,

  quick: `procedure quickSort(A, low, high)
    if low < high then
        p := partition(A, low, high)
        quickSort(A, low, p - 1)
        quickSort(A, p + 1, high)

procedure partition(A, low, high)
    pivot := A[high]
    i := low - 1
    for j := low to high - 1 do
        if A[j] <= pivot then
            i := i + 1
            swap A[i] and A[j]
    swap A[i + 1] and A[high]
    return i + 1`,

  merge: `procedure mergeSort(A, l, r)
    if l < r then
        m := floor((l + r) / 2)
        mergeSort(A, l, m)
        mergeSort(A, m + 1, r)
        merge(A, l, m, r)

procedure merge(A, l, m, r)
    L := A[l..m]; R := A[m+1..r]
    i := 0; j := 0; k := l
    while i < length(L) and j < length(R) do
        if L[i] <= R[j] then A[k] := L[i]; i++
        else A[k] := R[j]; j++
        k++
    copy remaining elements of L and R into A`,

  heap: `procedure heapSort(A)
    n := length(A)
    for i := floor(n / 2) - 1 downto 0 do
        heapify(A, n, i)
    for i := n - 1 downto 0 do
        swap A[0] and A[i]
        heapify(A, i, 0)

procedure heapify(A, n, i)
    largest := i
    left := 2 * i + 1; right := 2 * i + 2
    if left < n and A[left] > A[largest] then largest := left
    if right < n and A[right] > A[largest] then largest := right
    if largest != i then
        swap A[i] and A[largest]
        heapify(A, n, largest)`,

  insertion: `procedure insertionSort(A)
    for i := 1 to length(A) - 1 do
        key := A[i]
        j := i - 1
        while j >= 0 and A[j] > key do
            A[j + 1] := A[j]
            j := j - 1
        A[j + 1] := key`,

  selection: `procedure selectionSort(A)
    n := length(A)
    for i := 0 to n - 1 do
        minIdx := i
        for j := i + 1 to n - 1 do
            if A[j] < A[minIdx] then
                minIdx := j
        swap A[i] and A[minIdx]`,

  dijkstra: `procedure Dijkstra(Graph, source)
    for each vertex v in Graph do
        dist[v] := INFINITY
    dist[source] := 0
    priorityQueue := all vertices keyed by dist
    while priorityQueue is not empty do
        u := vertex in priorityQueue with minimum dist
        remove u from priorityQueue
        for each neighbor v of u with weight w do
            alt := dist[u] + w
            if alt < dist[v] then
                dist[v] := alt
                prev[v] := u
    return dist`,

  bfs: `procedure BFS(Graph, start)
    queue := [start]
    visited := {start}
    while queue is not empty do
        vertex := queue.dequeue()
        process(vertex)
        for each neighbor of vertex do
            if neighbor not in visited then
                visited.add(neighbor)
                queue.enqueue(neighbor)`,

  dfs: `procedure DFS(Graph, vertex)
    visited.add(vertex)
    process(vertex)
    for each neighbor of vertex do
        if neighbor not in visited then
            DFS(Graph, neighbor)`,

  'binary-search': `procedure binarySearch(A, target)
    low := 0; high := length(A) - 1
    while low <= high do
        mid := floor((low + high) / 2)
        if A[mid] == target then return mid
        else if A[mid] < target then low := mid + 1
        else high := mid - 1
    return NOT_FOUND`,

  redblack: `procedure RBInsert(root, key)
    node := new RedBlackNode(key)
    root := BSTInsert(root, node)
    fixInsert(root, node)
    return root

procedure fixInsert(root, node)
    while node.parent.color == RED do
        if node.parent == node.parent.parent.left then
            uncle := node.parent.parent.right
            if uncle.color == RED then
                node.parent.color := BLACK
                uncle.color := BLACK
                node.parent.parent.color := RED
                node := node.parent.parent
            else
                if node == node.parent.right then
                    node := node.parent
                    leftRotate(root, node)
                node.parent.color := BLACK
                node.parent.parent.color := RED
                rightRotate(root, node.parent.parent)
    root.color := BLACK`,
};

export function getLibraryPseudocode(name: string): string | null {
  const n = name.toLowerCase();
  for (const [key, value] of Object.entries(PSEUDOCODE_LIBRARY)) {
    if (n.includes(key)) return value;
  }
  return null;
}
